import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ISystemPaymentQuery,
  ITariffSort,
  ITariffSystemPayment,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { SortsStore } from '../sorts/sorts-store.service';
import { OnDemandAvailabilityStore } from './on-demand-availability-store.service';
import { OnDemandComfortPaymentGateway } from './on-demand-comfort-payment.gateway';

@Injectable({ providedIn: 'root' })
export class OnDemandComfortPaymentStore {
  private readonly gateway: OnDemandComfortPaymentGateway = inject(
    OnDemandComfortPaymentGateway
  );
  private readonly availabilityStore: OnDemandAvailabilityStore = inject(
    OnDemandAvailabilityStore
  );
  private readonly sortsStore: SortsStore = inject(SortsStore);

  private readonly configuredSortExternalReference: Signal<string | null> =
    computed(
      () =>
        this.availabilityStore.matchedMapping()?.comfortSortExternalRef ?? null
    );

  private readonly queryParams: Signal<ISystemPaymentQuery | undefined> =
    computed(
      () => {
        const externalReference = this.configuredSortExternalReference();
        if (!externalReference) {return undefined;}

        const sort: ITariffSort | undefined = this.sortsStore
          .sorts()
          .find(item => item.externalReference === externalReference);
        const tariffAreaId = sort?.serviceArea?.id;
        if (tariffAreaId === undefined) {return undefined;}

        const leg = this.availabilityStore.onDemandLeg();
        const dateTime =
          leg?.origin?.date && leg.origin.time
            ? `${leg.origin.date}T${leg.origin.time}`
            : undefined;

        return {
          tariffAreaId,
          externalReference,
          dateTime,
        };
      },
      {
        equal: (previous, current) =>
          previous?.tariffAreaId === current?.tariffAreaId &&
          previous?.externalReference === current?.externalReference &&
          previous?.dateTime === current?.dateTime,
      }
    );

  readonly #resource: ResourceRef<
    IApiResponse<ITariffSystemPayment[]> | undefined
  > = resource({
    params: this.queryParams,
    loader: ({ params }) =>
      params
        ? firstValueFrom(this.gateway.getSystemPayments(params))
        : Promise.resolve(undefined),
  });

  readonly systemPayments: Signal<ITariffSystemPayment[]> = computed(
    () => this.#resource.value()?.data ?? []
  );
}
